import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, RefreshCw } from 'lucide-react';
import { AppraisalDataSafety } from './AppraisalDataSafety';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

export default function AppraisalKPPManagement() {
  const [appraisalKpps, setAppraisalKpps] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    name: '', description: '', kpp_type: 'quantitative', level: 'Individual',
    weightage: 10, measurement_type: 'score', min_value: 1, max_value: 5
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchAppraisalKPPs(); }, []);

  const fetchAppraisalKPPs = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/appraisal/kpps`, config);
      setAppraisalKpps(response.data);
    } catch (error) {
      console.error('Error fetching Appraisal KPPs:', error);
    }
  };

  const handleToggleStatus = async (kppId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API}/appraisal/kpps/${kppId}/toggle-status`, {}, config);
      toast.success(response.data.message);
      fetchAppraisalKPPs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle status'));
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/appraisal/kpps/seed-defaults`, {}, config);
      toast.success(response.data.message);
      fetchAppraisalKPPs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to seed defaults'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editing) {
        await axios.put(`${API}/appraisal/kpps/${editing.id}`, form, config);
        toast.success('Appraisal KPP updated successfully');
      } else {
        await axios.post(`${API}/appraisal/kpps`, form, config);
        toast.success('Appraisal KPP created successfully');
      }
      setShowDialog(false);
      setEditing(null);
      setForm(emptyForm);
      fetchAppraisalKPPs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save Appraisal KPP'));
    }
  };

  const handleEdit = (kpp) => {
    setEditing(kpp);
    setForm({
      name: kpp.name, description: kpp.description || '',
      kpp_type: kpp.kpp_type, weightage: kpp.weightage, level: kpp.level || 'Individual',
      measurement_type: kpp.measurement_type, min_value: kpp.min_value, max_value: kpp.max_value
    });
    setShowDialog(true);
  };

  const handleDelete = async (kppId) => {
    if (!window.confirm('Are you sure you want to deactivate this Appraisal KPP?')) return;
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/appraisal/kpps/${kppId}`, config);
      toast.success('Appraisal KPP deactivated');
      fetchAppraisalKPPs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete Appraisal KPP'));
    }
  };

  const totalWeightage = appraisalKpps.filter(k => k.is_active !== false).reduce((sum, k) => sum + (k.weightage || 0), 0);

  return (
    <div className="space-y-6">
    <Card data-testid="appraisal-kpp-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Appraisal KPP Management</CardTitle>
          <CardDescription>Manage Key Performance Parameters for yearly appraisals</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedDefaults} data-testid="seed-appraisal-kpp-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Seed Defaults
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) {
              setEditing(null);
              setForm(emptyForm);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="add-appraisal-kpp-button">
                <Plus className="w-4 h-4 mr-2" /> Add Appraisal KPP
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Appraisal KPP' : 'Add Appraisal KPP'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="appraisal-kpp-name" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="appraisal-kpp-description" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.kpp_type} onValueChange={(val) => setForm({...form, kpp_type: val})}>
                      <SelectTrigger data-testid="appraisal-kpp-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quantitative" data-testid="appraisal-kpp-type-quantitative">Quantitative</SelectItem>
                        <SelectItem value="qualitative" data-testid="appraisal-kpp-type-qualitative">Qualitative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Level</Label>
                    <Select value={form.level} onValueChange={(val) => setForm({...form, level: val})}>
                      <SelectTrigger data-testid="appraisal-kpp-level-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Individual" data-testid="appraisal-kpp-level-individual">Individual</SelectItem>
                        <SelectItem value="Manager" data-testid="appraisal-kpp-level-manager">Manager</SelectItem>
                        <SelectItem value="Team Lead" data-testid="appraisal-kpp-level-team-lead">Team Lead</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Weightage (%)</Label><Input type="number" min={1} max={100} value={form.weightage} onChange={(e) => setForm({...form, weightage: Math.max(1, parseInt(e.target.value) || 1)})} data-testid="appraisal-kpp-weightage" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Measurement</Label>
                    <Select value={form.measurement_type} onValueChange={(val) => setForm({...form, measurement_type: val})}>
                      <SelectTrigger data-testid="appraisal-kpp-measurement-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="score" data-testid="appraisal-kpp-measurement-score">Score</SelectItem>
                        <SelectItem value="percentage" data-testid="appraisal-kpp-measurement-percentage">Percentage</SelectItem>
                        <SelectItem value="rating" data-testid="appraisal-kpp-measurement-rating">Rating</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Min Value</Label><Input type="number" value={form.min_value} onChange={(e) => setForm({...form, min_value: parseFloat(e.target.value)})} data-testid="appraisal-kpp-min-value" /></div>
                  <div><Label>Max Value</Label><Input type="number" value={form.max_value} onChange={(e) => setForm({...form, max_value: parseFloat(e.target.value)})} data-testid="appraisal-kpp-max-value" /></div>
                </div>
                <Button type="submit" className="w-full" data-testid="save-appraisal-kpp-button">{editing ? 'Update KPP' : 'Create KPP'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Badge variant={totalWeightage === 100 ? 'default' : 'destructive'} className="text-sm" data-testid="total-weightage-badge">
            Total Weightage: {totalWeightage}% {totalWeightage !== 100 && '(Should be 100%)'}
          </Badge>
        </div>
        <Table data-testid="appraisal-kpp-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Weightage</TableHead>
              <TableHead>Measurement</TableHead>
              <TableHead>Range</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appraisalKpps.map((kpp) => (
              <TableRow key={kpp.id} data-testid={`appraisal-kpp-row-${kpp.id}`}>
                <TableCell className="font-medium">{kpp.name}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{kpp.kpp_type}</Badge></TableCell>
                <TableCell><Badge variant="secondary">{kpp.level || 'Individual'}</Badge></TableCell>
                <TableCell>{kpp.weightage}%</TableCell>
                <TableCell className="capitalize">{kpp.measurement_type}</TableCell>
                <TableCell>{kpp.min_value} - {kpp.max_value}</TableCell>
                <TableCell>
                  <Switch checked={kpp.is_active !== false} onCheckedChange={() => handleToggleStatus(kpp.id)} data-testid={`appraisal-kpp-toggle-${kpp.id}`} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(kpp)} data-testid={`edit-appraisal-kpp-${kpp.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(kpp.id)} data-testid={`delete-appraisal-kpp-${kpp.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {appraisalKpps.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No Appraisal KPPs found. Click "Seed Defaults" to add standard parameters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    <AppraisalDataSafety onWipeComplete={fetchAppraisalKPPs} />
    </div>
  );
}
